/**
 * Wave 6 — customer submission history panel (family offer timeline).
 */
import React from 'react';
import { Alert, Card, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import quotationService from '@/services/quotation.service';
import { formatMoney, formatDateTime, UAE_MONEY_PROFILE } from '@/utils/format';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';

const { Text } = Typography;

const ROLE_TAG: Record<string, { color: string; label: string }> = {
  current_offer: { color: 'blue', label: 'Current offer' },
  accepted: { color: 'green', label: 'Accepted' },
  superseded: { color: 'default', label: 'Superseded' },
  rejected: { color: 'error', label: 'Rejected' },
  draft_tip: { color: 'gold', label: 'Draft tip' },
  other: { color: 'default', label: 'Other' },
};

type Props = {
  quotationId: string;
  embedded?: boolean;
};

export const QuotationSubmissionHistoryPanel: React.FC<Props> = ({ quotationId, embedded }) => {
  const historyQuery = useQuery({
    queryKey: ['quotation-submission-history', quotationId],
    queryFn: () => quotationService.listSubmissionHistory(quotationId),
  });

  const rows = historyQuery.data || [];
  const current = rows.find((r) => r.is_current_submitted);

  const body = (
    <>
      {current ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            <span>
              Current customer offer:{' '}
              <Link to={`/growth/quotations/${current.id}`}>
                {current.quotation_number} · {current.display_revision}
              </Link>{' '}
              ({current.status})
            </span>
          }
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="No current customer-submitted offer in this family (Draft tip may exist without superseding prior Sent)."
        />
      )}

      <Table
        size="small"
        rowKey="id"
        pagination={false}
        loading={historyQuery.isLoading}
        dataSource={rows}
        columns={[
          {
            title: 'Revision',
            key: 'rev',
            render: (_, r) => (
              <Link to={`/growth/quotations/${r.id}`}>
                {r.display_revision || displayQuotationRevisionLabel(r.revision_number)}
              </Link>
            ),
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (s: string) => <Tag>{s}</Tag>,
          },
          {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => {
              const meta = ROLE_TAG[role] || ROLE_TAG.other;
              return <Tag color={meta.color}>{meta.label}</Tag>;
            },
          },
          {
            title: 'Sent',
            dataIndex: 'sent_date',
            key: 'sent',
            render: (d: string | null) => (d ? formatDateTime(d) : '—'),
          },
          {
            title: 'Superseded',
            dataIndex: 'superseded_at',
            key: 'superseded',
            render: (d: string | null) => (d ? formatDateTime(d) : '—'),
          },
          {
            title: 'Value',
            key: 'value',
            render: (_, r) =>
              formatMoney(r.grand_total, { ...UAE_MONEY_PROFILE, currency: r.currency || 'AED' }),
          },
        ]}
      />
      <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
        Creating a Draft revision does not supersede the current Sent/Accepted offer. Sending a newer
        approved revision marks prior offers as Revised.
      </Text>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Card size="small" title="Customer submission history" style={{ marginTop: 16 }}>
      {body}
    </Card>
  );
};
